import { Page } from 'components/Page';
import React from 'react';
import { View } from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import {
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { EngageLoader } from './EngageLoader';
import { ScheduledVisitsHeader } from './ScheduledVisitsHeader';
import { PageEmpty } from 'components/PageEmpty';
import {
  EngageScheduledVisitListItem,
  SCHEDULED_VISIT_COMMUNICATION_REQUEST_DAYS,
} from 'types/Engage';
import { EngageScheduledVisitStatus } from 'types/DTOs/BlockManagerDTOs';
import { Grid, GridProps } from 'components/Grid/Grid';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectEngageScheduledVisitItem,
  selectEngageScheduledVisitsIds,
} from 'store/remote/engageSelectors';
import { doEngageScheduledVisitsLoad } from 'store/remote/engageActions';
import { Txt } from 'components/Txt';
import { differenceInCalendarDays, format } from 'date-fns';
import { theme } from 'lib/theme';
import { capitalize } from 'lodash';
import { Icon, ICONS } from 'components/Icon';
import { ISODateString } from 'types/types';
import { isoDateStringToDate } from 'lib/riskbase';
import { NAVIGATE_MODAL } from 'store/app/actions';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.Engage],
  Screens.EngageScheduledVisits
>;

type DefinedGridProps = GridProps<
  'dates' | 'status' | 'daysToGo' | 'actions',
  string,
  {
    pageKey: string;
    onRowPress: (scheduledVisit: EngageScheduledVisitListItem) => void;
  }
>;

const useGetRowData: DefinedGridProps['getRowData'] = ({ id, extraData }) => {
  const scheduledVisit = useSelector((state) =>
    selectEngageScheduledVisitItem(state, extraData.pageKey, id),
  );

  const getStatusColour = (status: EngageScheduledVisitStatus) => {
    if (status === EngageScheduledVisitStatus.PUBLISHED) {
      return theme.colors.green;
    }
    if (status === EngageScheduledVisitStatus.CANCELLED) {
      return theme.colors.red;
    }

    // draft status
    return theme.colors.black30;
  };

  if (!scheduledVisit) {
    return null;
  }

  const formatDate = (dateString: ISODateString | null) => {
    if (!dateString || dateString === undefined) {
      return '';
    }

    try {
      return format(isoDateStringToDate(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  return {
    onPress: () => {
      extraData.onRowPress(scheduledVisit);
    },
    columns: {
      actions: () => null,
      dates: () => (
        <Txt>
          {!scheduledVisit.startDateTime || !scheduledVisit.endDateTime
            ? 'No Date'
            : formatDate(scheduledVisit.startDateTime) +
              ' - ' +
              formatDate(scheduledVisit.endDateTime)}
        </Txt>
      ),
      status: () => {
        return (
          <View
            style={{
              flex: 1,
              flexDirection: 'row',
              columnGap: theme.paddingHorizontalHalf,
            }}
          >
            {scheduledVisit.startDateTime &&
              scheduledVisit.status === EngageScheduledVisitStatus.DRAFT &&
              differenceInCalendarDays(
                isoDateStringToDate(scheduledVisit.startDateTime),
                new Date(),
              ) <= SCHEDULED_VISIT_COMMUNICATION_REQUEST_DAYS && (
                <Icon
                  source={ICONS.TriangleExclamationDuotone}
                  style={{ tintColor: theme.colors.orange }}
                />
              )}
            <Txt
              weight="bold"
              size="small"
              style={{
                color: getStatusColour(scheduledVisit.status),
                display: 'flex',
              }}
            >
              {capitalize(scheduledVisit.status)}
            </Txt>
          </View>
        );
      },
      daysToGo: () => {
        if (!scheduledVisit.startDateTime) {
          return null;
        }

        const differenceInDays = differenceInCalendarDays(
          isoDateStringToDate(scheduledVisit.startDateTime),
          new Date(),
        );

        if (
          differenceInDays <= 0 ||
          scheduledVisit.status === EngageScheduledVisitStatus.CANCELLED ||
          scheduledVisit.status === EngageScheduledVisitStatus.DRAFT
        ) {
          return null;
        }

        return (
          <Txt>
            {differenceInDays + ' '}
            day{differenceInDays > 1 ? 's' : ''} to go
          </Txt>
        );
      },
    },
  };
};

export const ScheduledVisits: React.FC<Props> = ({
  navigation,
  route: {
    params: { pageKey, property },
  },
}) => {
  const dispatch = useDispatch();

  const handleOpenScheduledVisitModal = React.useCallback(
    (scheduledVisit?: EngageScheduledVisitListItem) => {
      dispatch(
        NAVIGATE_MODAL({
          screen: ModalScreens.EngageFlatDoorScheduledVisit,
          size: 'large',
          params: {
            propertyId: property.id,
            pageKey,
            scheduledVisit,
          },
        }),
      );
    },
    [dispatch, property.id, pageKey],
  );

  React.useEffect(() => {
    if (!property) {
      return;
    }

    loadInitialised.current = true;
    dispatch(
      doEngageScheduledVisitsLoad({
        key: pageKey,
        propertyId: property.id,
      }),
    );
  }, [dispatch, property, pageKey]);

  const handlePressBack = () => {
    navigation.pop();
  };

  const handleRowPress = (scheduledVisit: EngageScheduledVisitListItem) => {
    handleOpenScheduledVisitModal(scheduledVisit);
  };

  const [columns] = React.useState<DefinedGridProps['columns']>([
    {
      name: 'Dates',
      dataIndex: 'dates',
      minWidth: 200,
      flex: 2,
    },
    {
      name: 'Status',
      dataIndex: 'status',
      minWidth: 200,
      flex: 1,
    },
    {
      name: 'Days To Go',
      dataIndex: 'daysToGo',
      minWidth: 200,
      flex: 1,
    },
    {
      name: 'Actions',
      dataIndex: 'actions',
      width: 100,
    },
  ]);

  const gridIds = useSelector((state) =>
    selectEngageScheduledVisitsIds(state, pageKey),
  );

  const loadInitialised = React.useRef(!!gridIds?.length);

  return (
    <View style={{ position: 'relative', flex: 1 }}>
      <Page
        isInModal={false}
        contentPaddingTop={0}
        ignoreSafeAreaBottom
        header={
          <ScheduledVisitsHeader
            // isLoading={!loadInitialised.current}
            onBackPress={handlePressBack}
            property={property}
            pageKey={pageKey}
          />
        }
      >
        <EngageLoader pageKey={pageKey} />
        <Page isInModal={false} contentPaddingTop={0} ignoreSafeAreaBottom>
          {gridIds.length > 0 && (
            <Grid
              columns={columns}
              data={gridIds}
              extraData={{
                pageKey,
                onRowPress: handleRowPress,
              }}
              getRowData={useGetRowData}
              keyExtractor={(i) => `scheduled-visit-${i}`}
            />
          )}
          {!loadInitialised.current && (
            <PageEmpty
              text="Waiting..."
              style={{ backgroundColor: theme.colors.background }}
            />
          )}
          {gridIds.length === 0 && !!loadInitialised.current && (
            <PageEmpty
              text="No Scheduled Visits Found"
              subtext="Try creating a scheduled visit using the button above"
              iconCircled={false}
              style={{ backgroundColor: theme.colors.background }}
            />
          )}
        </Page>
      </Page>
    </View>
  );
};
